import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Button,
  Tabs,
  Tab,
  Grid,
  useMediaQuery,
  useTheme,
  Typography,
  CircularProgress,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CollapsibleSection from "../../pages/common-features/CollapsibleSection";
import DetailRow from "../../pages/common-features/DetailRow";
import applicationDetailsServices from "../../services/api/service/applicationDetails";
import losServices from "../../services/api/service/losService";
import { tabContentSkeleton } from "../../pages/summary/ApplicationDetails";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../services/redux/store";
import {
  setCoApplicantAddressDetails,
  setCoApplicantBasicDetails,
  setCoApplicantEmploymentDetails,
} from "../../services/redux/slices/applicationLoanDetailsSlice";
import {
  setActiveHorizontalIndex,
  setActiveVerticalIndex,
  setLoanDetailCoApplicantCollapseValue,
  setSummaryApplicationEditActive,
  setSummaryEditOpenedFromCoApplicantSection,
} from "../../services/redux/slices/stepperDynamicFormSlice";
import { useSelector as useReduxSelector } from "react-redux";
import { RootState as ReduxRootState } from "../../services/redux/store";
import { formatDOB, maskPan } from "../../utils/helpers";
import SummarySectionHeader from "./SummarySectionHeader";
import HorizontalLinearStepper from "../../pages/common-features/HorizontalLinearStepper";
import postsServices from "../../services/api/service/postsService";
import { setStatusPopup } from "../../services/redux/slices/statusPopupSlice";

interface SingleCoApplicantTabsProps {
  coApplicantFields: any[];
  coApplicantId: number | string;
  coApplicantNumber: number;
  title: string;
  isExpanded: boolean;
  onToggle: () => void;
  pageType?: string;
  onEditClick?: (coApplicantNumber: number, displayIndex?: number) => void;
  /** When true, summary Edit is disabled (prior journey stages incomplete). */
  editDisabled?: boolean;
  /** Map of pennant relationship codes to descriptions e.g. { BROINLW: "Brother in law" } */
  pennantRelMap?: Record<string, string>;
  designationsVal?: any[];
  hideEditAction?: boolean;
}

// Type for co-applicant field
interface CoApplicantField {
  label?: string;
  name?: string;
  key?: string;
  value?: any;
  isVerified?: boolean;
}
// Type for co-applicant field value
type CoApplicantFieldValue = string | number | boolean | null | undefined;

const tabLabels = ["Basic Details", "Address Details", "Employment Details"];
const isCoApplicantSameAsAddressField = (field: any) =>
  /^addressCoApplicant\d+(?:current)?sameAs(?:Permanent)?$/i.test(
    String(field?.name || field?.key || "")
  );

const transformCoApplicantObjectToArray = (input: any) => {
  if (!input || Array.isArray(input) || typeof input !== "object") return input;
  const entries = Object.entries(input).filter(([k]) => /^coapplicant\d+$/i.test(//C
    k
  ));
  if (entries.length === 0) return input;
  return entries
    .sort(([a], [b]) => {
      const numA = parseInt(String(a).replace(/[^\d]/g, ""), 10);
      const numB = parseInt(String(b).replace(/[^\d]/g, ""), 10);
      return numA - numB;
    })
    .map(([key, value]: [string, any]) => {
      const fields: any = [{}];
      return {
        [key]: fields,
        [`${key}Id`]: value?.coApplicantId,
      };
    });
};

function mapCoApplicantDetailsFromVerticalStep(verticalStep: any) {
  if (!verticalStep || !verticalStep?.fields) return [];
  const coAppMap: { [key: string]: any[] } = {};
  const coAppIdMap: { [key: string]: any } = {};
  verticalStep?.fields.forEach((fieldObj: any) => {
    const key: string = Object.keys(fieldObj)?.[0] as string;
    const value: CoApplicantFieldValue = fieldObj?.[key];
    const match: RegExpMatchArray | null =
      key.match(/CoApplicant(\d+)/i) || key.match(/coApplicant(\d+)/i);
    if (!match) return;
    const coAppNum = match[1];
    const coAppKey = `coapplicant${coAppNum}`;
    if (!coAppMap[coAppKey]) coAppMap[coAppKey] = [];

    function stripPrefixAndFormat(str: string) {
      const prefixPattern =
        /^(basicCoApplicant\d+|addressCoApplicant\d+|employmentCoApplicant\d+)/i;
      const noPrefix = str.replace(prefixPattern, "");
      return noPrefix
        .replace(/([A-Z])/g, " $1")
        .replace(/_/g, " ")
        .replace(/^./, (s) => s.toUpperCase())
        .trim();
    }
    let label = fieldObj?.label || stripPrefixAndFormat(key);

    if (/OtherRelationWithStudent/i.test(key)) label = "Specify Relationship";
    else if (/RelationWithStudent/i.test(key))
      label = "Relationship with Student";
    else if (/FirstName/i.test(key)) label = "Co-Applicant First Name";
    else if (/LastName/i.test(key)) label = "Co-Applicant Last Name";
    else if (/PanNumber/i.test(key)) label = "PAN Number";
    else if (/IsPanVerified/i.test(key)) label = "PAN Verified";
    else if (/Email$/i.test(key) && !/IsEmailVerified/i.test(key))
      label = "Email ID";
    else if (/IsEmailVerified/i.test(key)) label = "Email Verified";
    else if (/PhoneNumber|Phone_number/i.test(key)) label = "Mobile Number";
    else if (/IsMobileVerified/i.test(key)) label = "Mobile Verified";

    let name = key;
    if (/OtherRelationWithStudent/i.test(key))
      name = `coApplicant${coAppNum}OtherRelationWithStudent`;
    else if (/RelationWithStudent/i.test(key))
      name = `coApplicant${coAppNum}RelationWithStudent`;
    else if (/FirstName/i.test(key)) name = `coApplicant${coAppNum}FirstName`;
    else if (/LastName/i.test(key)) name = `coApplicant${coAppNum}LastName`;
    else if (/PanNumber/i.test(key)) name = `coApplicant${coAppNum}PanNumber`;
    else if (/IsPanVerified/i.test(key))
      name = `coApplicant${coAppNum}IsPanVerified`;
    else if (/Email$/i.test(key) && !/IsEmailVerified/i.test(key))
      name = `coApplicant${coAppNum}Email`;
    else if (/IsEmailVerified/i.test(key))
      name = `coApplicant${coAppNum}IsEmailVerified`;
    else if (/PhoneNumber|Phone_number/i.test(key))
      name = `coApplicant${coAppNum}PhoneNumber`;
    else if (/IsMobileVerified/i.test(key))
      name = `coApplicant${coAppNum}IsMobileVerified`;

    coAppMap[coAppKey].push({ label, name, key, value });
    if (/Id$/.test(key)) {
      coAppIdMap[coAppKey] = value;
    }
  });

  return Object.keys(coAppMap)
    .sort((a, b) => {
      const numA = parseInt(a.replace(/[^\d]/g, ""), 10);
      const numB = parseInt(b.replace(/[^\d]/g, ""), 10);
      return numA - numB;
    })
    .map((coAppKey) => ({
      [coAppKey]: coAppMap[coAppKey],
      [`${coAppKey}Id`]: coAppIdMap[coAppKey],
    }));
}

function mapCoApplicantDetailsFromFormStep(verticalStep: any) {
  if (!verticalStep || !Array.isArray(verticalStep?.fields)) return [];

  const coAppMap: { [key: string]: any[] } = {};
  const coAppIdMap: { [key: string]: any } = {};

  verticalStep.fields.forEach((field: any) => {
    const fieldName = String(field?.name || "");
    const match = fieldName.match(
      /^(basic|address|employment)CoApplicant(\d+)/i
    );
    if (!match) return;

    const coAppNum = match[2];
    const coAppKey = `coapplicant${coAppNum}`;
    if (!coAppMap[coAppKey]) coAppMap[coAppKey] = [];

    coAppMap[coAppKey].push({
      ...field,
      key: field?.key || fieldName,
      name: fieldName,
      label: field?.label || fieldName,
      value: field?.value ?? "",
    });

    if (/ApplicantId$|Id$/i.test(fieldName) && field?.value) {
      coAppIdMap[coAppKey] = field.value;
    }
  });

  return Object.keys(coAppMap)
    .sort((a, b) => {
      const numA = parseInt(a.replace(/[^\d]/g, ""), 10);
      const numB = parseInt(b.replace(/[^\d]/g, ""), 10);
      return numA - numB;
    })
    .map((coAppKey) => ({
      [coAppKey]: coAppMap[coAppKey],
      [`${coAppKey}Id`]: coAppIdMap[coAppKey],
    }));
}

const mergeCoApplicantDetails = (
  primaryData: any[] = [],
  fallbackData: any[] = []
) => {
  const normalizeCoApp = (item: any) => {
    const coAppKey = Object.keys(item || {}).find((k) =>
      String(k).toLowerCase().startsWith("coapplicant")
    );
    const coAppIdKey = coAppKey ? `${coAppKey}Id` : "";
    return {
      coAppKey,
      coAppIdKey,
      coAppId: coAppIdKey ? item?.[coAppIdKey] : undefined,
      fields: coAppKey ? item?.[coAppKey] || [] : [],
    };
  };

  const normalizeFieldKey = (field: any) =>
    String(field?.key ?? field?.name ?? "").toLowerCase();

  const fallbackMap = new Map<string, any>();
  fallbackData.forEach((item) => {
    const normalized = normalizeCoApp(item);
    if (normalized.coAppKey) {
      fallbackMap.set(normalized.coAppKey.toLowerCase(), normalized);
    }
  });

  const primaryMap = new Map<string, any>();
  primaryData.forEach((item) => {
    const normalized = normalizeCoApp(item);
    if (normalized.coAppKey) {
      primaryMap.set(normalized.coAppKey.toLowerCase(), normalized);
    }
  });

  const allKeys = new Set(
    Array.from(fallbackMap.keys()).concat(Array.from(primaryMap.keys()))
  );
  const merged: any[] = [];

  allKeys.forEach((key) => {
    const fallback = fallbackMap.get(key);
    const primary = primaryMap.get(key);
    const fallbackFields = fallback?.fields || [];
    const primaryFields = primary?.fields || [];
    const primaryFieldMap = new Map<string, any>();
    primaryFields.forEach((field: any) => {
      const fieldKey = normalizeFieldKey(field);
      if (fieldKey) primaryFieldMap.set(fieldKey, field);
    });

    const fallbackFieldMap = new Map<string, any>();
    fallbackFields.forEach((field: any) => {
      const fieldKey = normalizeFieldKey(field);
      if (fieldKey) fallbackFieldMap.set(fieldKey, field);
    });

    const mergedFields = fallbackFields.map((fallbackField: any) => {
      const fieldKey = normalizeFieldKey(fallbackField);
      const primaryField = fieldKey ? primaryFieldMap.get(fieldKey) : undefined;
      return primaryField
        ? { ...fallbackField, ...primaryField, value: primaryField?.value ?? "" }
        : { ...fallbackField, value: fallbackField?.value ?? "" };
    });

    primaryFields.forEach((primaryField: any) => {
      const fieldKey = normalizeFieldKey(primaryField);
      if (!fieldKey || fallbackFieldMap.has(fieldKey)) return;
      mergedFields.push({ ...primaryField, value: primaryField?.value ?? "" });
    });

    const coAppKey = primary?.coAppKey || fallback?.coAppKey;
    if (!coAppKey) return;

    merged.push({
      [coAppKey]: mergedFields,
      [`${coAppKey}Id`]: primary?.coAppId ?? fallback?.coAppId,
    });
  });

  return merged.sort((a, b) => {
    const keyA = Object.keys(a).find((k) => k.startsWith("coapplicant")) || "";
    const keyB = Object.keys(b).find((k) => k.startsWith("coapplicant")) || "";
    const numA = parseInt(keyA.replace(/[^\d]/g, ""), 10);
    const numB = parseInt(keyB.replace(/[^\d]/g, ""), 10);
    return numA - numB;
  });
};

const SingleCoApplicantTabs: React.FC<SingleCoApplicantTabsProps> = ({
  coApplicantFields,
  coApplicantId,
  coApplicantNumber,
  title,
  isExpanded,
  onToggle,
  pageType = "DEFAULT",
  onEditClick,
  editDisabled = false,
  pennantRelMap = {},
  designationsVal = [],
  hideEditAction = false,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const theme = useTheme();
  const isXsOrSm = useMediaQuery(theme.breakpoints.down("sm"));
  const [tabIndex, setTabIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const fetchedSectionRef = React.useRef<Record<string, boolean>>({});
  const lastBasicDetailsRefreshNonceRef = React.useRef<number | null>(null);
  const {
    fetchUserApplicationData,
    trackingId,
    completionSummaryRefreshNonce,
  } = useReduxSelector((state: ReduxRootState) => state.stepper);
  const coApplicantBasicDetails = useSelector(
    (state: RootState) =>
      state.applicationLoanDetailsData.coApplicantBasicDetails?.[
        coApplicantId
      ] || []
  );
  const coApplicantAddressDetails = useSelector(
    (state: RootState) =>
      state.applicationLoanDetailsData.coApplicantAddressDetails?.[
        coApplicantId
      ] || {}
  );
  const coApplicantEmploymentDetails = useSelector(
    (state: RootState) =>
      state.applicationLoanDetailsData.coApplicantEmploymentDetails?.[
        coApplicantId
      ] || {}
  );

  const loadSectionData = async (newValue: number) => {
    // Only call APIs if partnerStartedJourney is true, applicantId and applicationId exist
    if (
      !fetchUserApplicationData?.partnerStartedJourney ||
      !fetchUserApplicationData?.applicantId ||
      !fetchUserApplicationData?.applicationId
    ) {
      return;
    }
    const fetchKey = `${trackingId || ""}:${coApplicantId}:${newValue}`;
    const shouldRefreshBasicDetails =
      newValue === 0 &&
      lastBasicDetailsRefreshNonceRef.current !==
        completionSummaryRefreshNonce;

    if (
      newValue === 0 &&
      ((!coApplicantBasicDetails || coApplicantBasicDetails.length === 0) ||
        shouldRefreshBasicDetails)
    ) {
      if (fetchedSectionRef.current[fetchKey] && !shouldRefreshBasicDetails) return;
      lastBasicDetailsRefreshNonceRef.current =
        completionSummaryRefreshNonce;
      fetchedSectionRef.current[fetchKey] = true;
      setLoading(true);
      try {
        const res = await applicationDetailsServices.getCoApplicantBasicDetails(
          String(coApplicantId),
          trackingId || ""
        );
        if (res && res.data) {
          const arr = res.data?.data || [];

          const updatedArr = arr.map(
            (item: { label: string; value: string | boolean }) =>
              item.label === 'Pay Party' && typeof item.value === 'boolean'
                ? { ...item, value: item.value ? 'Yes' : 'No' }
                : item
          );

          dispatch(
            setCoApplicantBasicDetails({
              coApplicantId,
              basicDetails: Array.isArray(updatedArr) ? updatedArr : [],
            })
          );
        }
      } catch (e) {
        dispatch(
          setCoApplicantBasicDetails({ coApplicantId, basicDetails: [] })
        );
      } finally {
        setLoading(false);
      }
    }

    if (
      newValue === 1 &&
      (!coApplicantAddressDetails ||
        (!coApplicantAddressDetails.currentAddress &&
          !coApplicantAddressDetails.permanentAddress))
    ) {
      if (fetchedSectionRef.current[fetchKey]) return;
      fetchedSectionRef.current[fetchKey] = true;
      setLoading(true);
      try {
        const res = await applicationDetailsServices.getApplicationLoanDetails(
          String(coApplicantId),
          trackingId || ""
        );
        if (res && res.data) {
          const addressObj =
            res.data.data?.["Application&LoanDetails"]?.AddressDetails;
          dispatch(
            setCoApplicantAddressDetails({
              coApplicantId,
              addressDetails: addressObj || {},
            })
          );
        }
      } catch (e) {
        dispatch(
          setCoApplicantAddressDetails({ coApplicantId, addressDetails: {} })
        );
      } finally {
        setLoading(false);
      }
    }

    if (
      newValue === 2 &&
      (!coApplicantEmploymentDetails ||
        (!coApplicantEmploymentDetails.EmploymentDetails &&
          !coApplicantEmploymentDetails.CompanyAddressDetails))
    ) {
      if (fetchedSectionRef.current[fetchKey]) return;
      fetchedSectionRef.current[fetchKey] = true;
      setLoading(true);
      try {
        const res = await applicationDetailsServices.getEmploymentDetails(
          String(coApplicantId),
          trackingId || ""
        );
        if (res && res.data) {
          dispatch(
            setCoApplicantEmploymentDetails({
              coApplicantId,
              employmentDetails: res.data.data?.EmploymentDetails || {},
            })
          );
        }
      } catch (e) {
        dispatch(
          setCoApplicantEmploymentDetails({
            coApplicantId,
            employmentDetails: {},
          })
        );
      } finally {
        setLoading(false);
      }
    }
  };

  const handleTabChange = async (
    _event: React.SyntheticEvent,
    newValue: number
  ) => {
    setTabIndex(newValue);
    await loadSectionData(newValue);
  };

  // Fetch basic details when the section expands and Basic tab is active
  useEffect(() => {
    if (!isExpanded || tabIndex !== 0) return;
    loadSectionData(0);
  }, [
    isExpanded,
    tabIndex,
    fetchUserApplicationData?.partnerStartedJourney,
    fetchUserApplicationData?.applicantId,
    fetchUserApplicationData?.applicationId,
    coApplicantId,
    trackingId,
    completionSummaryRefreshNonce,
  ]);

  const getFieldIdentity = (field: any) =>
    String(field?.key || field?.name || "").trim();

  // Split address fields from coApplicantFields for Address Details tab
  const currentAddressFields = coApplicantFields.filter(
    (f) => /^addressCoApplicant\d+current/i.test(getFieldIdentity(f))
  );
  const permanentAddressFields = coApplicantFields.filter(
    (f) => /^addressCoApplicant\d+permanent/i.test(getFieldIdentity(f))
  );
  // Split employment fields for Employment Details tab
  const employmentFields = coApplicantFields.filter(
    (f) => /^employmentCoApplicant\d+/i.test(getFieldIdentity(f))
  );
  // Remove address and employment fields from basic details
  const basicFields = coApplicantFields.filter(
    (f) => {
      const fieldIdentity = getFieldIdentity(f);
      return (
        !/^addressCoApplicant\d+/i.test(fieldIdentity) &&
        !/^employmentCoApplicant\d+/i.test(fieldIdentity)
      );
    }
  );
  const isCompanyAddressField = (field: any) => {
    const identity = String(field?.key || field?.name || "").toLowerCase();
    const label = String(field?.label || "").toLowerCase();
    const combined = `${identity} ${label}`;

    return (
      /companyaddress[123]|addressline[123]|address line [123]|landmark|pincode|city|state|country/.test(
        combined
      ) && !/companyname|company name/.test(combined)
    );
  };

  const getCompanyAddressFieldOrder = (field: any) => {
    const combined = `${field?.key || field?.name || ""} ${field?.label || ""}`.toLowerCase();

    if (/companyaddress1|addressline1|address line 1/.test(combined)) return 1;
    if (/companyaddress2|addressline2|address line 2/.test(combined)) return 2;
    if (/companyaddress3|addressline3|address line 3/.test(combined)) return 3;
    if (/landmark/.test(combined)) return 4;
    if (/pincode/.test(combined)) return 5;
    if (/city/.test(combined)) return 6;
    if (/state/.test(combined)) return 7;
    if (/country/.test(combined)) return 8;
    return 99;
  };

  const getEmploymentDisplayLabel = (field: any) => {
    const combined = `${field?.key || field?.name || ""} ${field?.label || ""}`.toLowerCase();

    if (/(^|[^a-z])isemployed|is employed/.test(combined)) return "Are you employed?";
    if (/employmenttype|employment type/.test(combined)) return "Type";
    if (/employmentyear|employment year/.test(combined)) return "Year";
    if (/employmentmonth|employment month/.test(combined)) return "Month";
    if (/companyname|company name/.test(combined)) return "Company Name";
    return field.label;
  };

  const getCompanyAddressDisplayLabel = (field: any) => {
    const combined = `${field?.key || field?.name || ""} ${field?.label || ""}`.toLowerCase();

    if (/companyaddress1|addressline1|address line 1/.test(combined)) return "Address Line 1";
    if (/companyaddress2|addressline2|address line 2/.test(combined)) return "Address Line 2";
    if (/companyaddress3|addressline3|address line 3/.test(combined)) return "Address Line 3";
    if (/landmark/.test(combined)) return "Landmark";
    if (/pincode/.test(combined)) return "Pincode";
    if (/city/.test(combined)) return "City";
    if (/state/.test(combined)) return "State";
    if (/country/.test(combined)) return "Country";
    return field.label;
  };

  // Further split employment fields into Employment Details and Company Address
  const companyAddressFields = employmentFields.filter(isCompanyAddressField);
  const employmentDetailsFields = employmentFields.filter(
    (f) => !isCompanyAddressField(f)
  );

  const isAddressField = (field: any, section: "current" | "permanent") => {
    const key = String(field?.key || field?.name || "").toLowerCase();
    const label = String(field?.label || "").toLowerCase();

    // Strong positive checks for co-applicant address keys
    if (
      new RegExp(`^addresscoapplicant\\d+${section}`).test(key) ||
      key.includes("currentsameaspermanent")
    ) {
      return true;
    }

    // Positive checks for generic address payloads
    const hasAddressWord = /address|pincode|pin code|city|state|country|ownership|same as permanent|years in current/.test(
      label
    );
    const hasSectionWord = section === "current" ? /current/.test(label) : /permanent/.test(label);

    if (hasAddressWord && hasSectionWord) {
      return true;
    }

    // Guard against non-address fields leaking into address section
    const nonAddressKeyOrLabel =
      /firstname|lastname|relation|student|salutation|pan|email|mobile|phone|dob|employ|designation|income/.test(
        key
      ) ||
      /first name|last name|relationship|salutation|pan|email|mobile|phone|dob|employ|designation|income/.test(
        label
      );

    if (nonAddressKeyOrLabel) {
      return false;
    }

    return false;
  };

  const getBasicCanonicalFieldKey = (field: CoApplicantField) => {
    const raw = String(field?.name || field?.key || "").toLowerCase();
    const label = String(field?.label || "").toLowerCase();
    const combined = `${raw} ${label}`;

    if (/firstname|first name/.test(combined)) return "first-name";
    if (/lastname|last name/.test(combined)) return "last-name";
    if (
      /otherrelationwithstudent|other relationship|specify relationship/.test(
        combined
      )
    ) {
      return "other-relation-with-student";
    }
    if (/relationwithstudent|relationship with student/.test(combined))
      return "relation-with-student";
    if (/salutation/.test(combined)) return "salutation";
    if (/dob|date of birth/.test(combined)) return "dob";
    if (/pannumber|pan number/.test(combined)) return "pan-number";
    if (/email/.test(combined) && !/isemailverified|email verified/.test(combined))
      return "email";
    if (/phone|mobile/.test(combined) && !/ismobileverified|mobile verified/.test(combined))
      return "phone";
    if (/aadhaar|aadhar/.test(combined)) return "aadhaar";

    return raw || label;
  };

  const isBasicField = (field: any) => {
    const identity = getFieldIdentity(field);
    const label = String(field?.label || "").toLowerCase();
    const combined = `${identity} ${label}`.toLowerCase();
    if (/^addressCoApplicant\d+/i.test(identity)) return false;
    if (/^employmentCoApplicant\d+/i.test(identity)) return false;
    if (/countryofbirth|cityofbirth|country of birth|city of birth/i.test(combined)) {
      return true;
    }
    if (
      /current address|permanent address|pincode|city|state|country|ownership|employment|designation|company|office|work|monthly income|monthly obligation/i.test(
        label
      )
    ) {
      return false;
    }
    return true;
  };

  const isEmploymentField = (field: any) => {
    const key = String(field?.key || field?.name || "").toLowerCase();
    const label = String(field?.label || "").toLowerCase();
    const combined = `${key} ${label}`;

    // Strong positive checks for employment payloads
    if (/^employmentcoapplicant\d+/.test(key)) {
      return true;
    }

    // Explicitly block basic/address leakage
    if (
      /firstname|last name|lastname|relation|student|salutation|pan|email|mobile|phone|dob|date of birth|address|pincode|city|state|country|ownership/.test(
        combined
      )
    ) {
      return false;
    }

    if (
      /is employed|employment type|designation|employer|company|office|work|monthly income|monthly obligation|profession|industry/.test(
        label
      )
    ) {
      return true;
    }

    return false;
  };

  const getDesignationLabel = (value: any) => {
    if (!value) return value;
    const code =
      typeof value === "object"
        ? value?.value ?? value?.genDesignation ?? value?.label
        : value;
    const match = designationsVal?.find((designation: any) => {
      const designationCode =
        designation?.value ?? designation?.genDesignation ?? designation?.code;
      const designationLabel =
        designation?.label ?? designation?.genDesgDesc ?? designation?.value;
      return designationCode === code || designationLabel === code;
    });

    return match?.label ?? match?.genDesgDesc ?? value;
  };
  const isVerifiedFlag = (value: any) =>
    value === true || value === "true" || value === 1 || value === "1";

  const getCoApplicantNumberFromField = (fieldName: string) => {
    const match = String(fieldName || "").match(/coApplicant(\d+)/i);
    return match?.[1] || "";
  };

  const isPartnerJourney = !!fetchUserApplicationData?.partnerStartedJourney;

  const renderBasicSection = () => {
    const basicData =
      isPartnerJourney
        ? coApplicantBasicDetails
        : coApplicantBasicDetails && coApplicantBasicDetails.length > 0
          ? coApplicantBasicDetails
          : basicFields;
    return (
      <Grid container spacing={2}>
        {loading ? (
          tabContentSkeleton()
        ) : basicData?.length > 0 ? (
          <>
            {basicData
            ?.filter((field: CoApplicantField) => {
              if (!isBasicField(field)) {
                return false;
              }
              if (/^(basic)?coApplicant\d+IsPanVerified$/i.test(field?.name || field?.key || "")) {
                return false;
              }
	              if (
	                /^(basic)?coApplicant\d+IsMobileVerified$/i.test(field?.name || field?.key || "")
	              ) {
	                return false;
	              }
	              if (/^(basic)?coApplicant\d+IsEmailVerified$/i.test(field?.name || field?.key || "")) {
	                return false;
	              }
              return true;
            })
            .map((field: CoApplicantField, idx: number) => {
              let isVerified = false;
              const effectiveBasicFields = (
                isPartnerJourney
                  ? coApplicantBasicDetails
                  : coApplicantBasicDetails && coApplicantBasicDetails.length > 0
                    ? coApplicantBasicDetails
                    : basicFields
              ) as CoApplicantField[];
              const fieldName = field?.name || field?.key || "";
              if (
                getBasicCanonicalFieldKey(field) ===
                "other-relation-with-student"
              ) {
                const relationship = effectiveBasicFields.find(
                  (item) =>
                    getBasicCanonicalFieldKey(item) ===
                    "relation-with-student"
                )?.value;
                const relationshipValue = String(
                  pennantRelMap[String(relationship ?? "")] || relationship || ""
                ).toLowerCase();
                if (!["other", "others"].includes(relationshipValue)) {
                  return null;
                }
              }
	              const isCoApplicantPhone =
	                /^basicCoApplicant\d+PhoneNumber$/i.test(fieldName) ||
	                /^coApplicant\d+Phone_?number$/i.test(fieldName);
              const isCoApplicantEmail =
                /^basicCoApplicant\d+Email$/i.test(fieldName) ||
                /^coApplicant\d+Email$/i.test(fieldName);
              const isCoApplicantPan =
                /^basicCoApplicant\d+PanNumber$/i.test(fieldName) ||
                /^coApplicant\d+PanNumber$/i.test(fieldName);

	              if (isCoApplicantPhone) {
	                const coApplicantNumber = getCoApplicantNumberFromField(fieldName);
	                const verifiedField = effectiveBasicFields?.find((f) => {
	                  const name = f?.name || f?.key || "";
	                  return (
	                    getCoApplicantNumberFromField(name) === coApplicantNumber &&
	                    /^(basic)?coApplicant\d+IsMobileVerified$/i.test(name) &&
	                    isVerifiedFlag(f?.value)
	                  );
	                });
                if (verifiedField) {
                  isVerified = true;
                }
              }

              if (isCoApplicantEmail) {
                const coApplicantNumber = getCoApplicantNumberFromField(fieldName);
                const verifiedField = effectiveBasicFields?.find((f) => {
                  const name = f?.name || f?.key || "";
                  return (
                    getCoApplicantNumberFromField(name) === coApplicantNumber &&
                    /^(basic)?coApplicant\d+IsEmailVerified$/i.test(name) &&
                    isVerifiedFlag(f?.value)
                  );
                });
                if (verifiedField) {
                  isVerified = true;
                }
              }

              if (isCoApplicantPan) {
                const coApplicantNumber = getCoApplicantNumberFromField(fieldName);
                const verifiedField: CoApplicantField | undefined =
                  effectiveBasicFields?.find((f: CoApplicantField) => {
                    const name = f?.name || f?.key || "";
                    return (
                      getCoApplicantNumberFromField(name) === coApplicantNumber &&
                      /^(basic)?coApplicant\d+IsPanVerified$/i.test(name) &&
                      isVerifiedFlag(f?.value)
                    );
                  });
                if (verifiedField) {
                  isVerified = true;
                }
              }

              const skipNames = ["aadhaar"];
              if (
                field?.name &&
                skipNames.some(
                  (skip) => field.name && field.name.toLowerCase().includes(skip)
                ) &&
                /(coApplicant\d+|basicCoApplicant\d+)/i.test(field.name)
              ) {
                return null;
              }

              const isDobField =
                /^basicCoApplicant\d+dob$/.test(fieldName) ||
                /^coApplicant\d+Dob$/.test(fieldName);

              const isRelationField = /RelationWithStudent/i.test(field?.name || field?.key || "");
              let displayValue = field?.value;
              if (isDobField) {
                displayValue = formatDOB(field?.value);
              } else if (isCoApplicantPan) {
                displayValue = maskPan(field?.value);
              } else if (isRelationField) {
                displayValue = pennantRelMap[field?.value] || field?.value;
              }

              return (
                <Grid
                  item
                  xs={12}
                  sm={6}
                  md={3}
                  key={field?.name || field?.key || idx}
                  sx={{ ml: isXsOrSm ? 2 : 0 }}
                >
                  <DetailRow
                    item={{
                      ...field,
	                      label: field?.label ?? "",
	                      value: displayValue,
	                      isVerified,
		                      showNotVerified:
		                        (isCoApplicantPhone || isCoApplicantEmail || isCoApplicantPan) &&
		                        String(displayValue ?? "").trim() !== "" &&
		                        !isVerified,
	                    }}
	                  />
                </Grid>
              );
            })
            }
          </>
        ) : (
          <Grid item xs={12} sx={{ mt: 2, mb: 1 }}>
            <Typography color="text.secondary" fontSize={14}>
              No details available.
            </Typography>
          </Grid>
        )}
      </Grid>
    );
  };

  const renderAddressSection = () => (
    <Grid container spacing={2}>
      {loading ? (
        tabContentSkeleton()
      ) : (
        <>
          {(() => {
            const currentAddressData =
              coApplicantAddressDetails?.currentAddress &&
              coApplicantAddressDetails.currentAddress.length > 0
                ? coApplicantAddressDetails.currentAddress.filter(
                    (field: any) =>
                      !isCoApplicantSameAsAddressField(field) &&
                      (isPartnerJourney ? true : isAddressField(field, "current"))
                  )
                : isPartnerJourney ? [] : currentAddressFields.filter((field: any) =>
                    !isCoApplicantSameAsAddressField(field) &&
                    isAddressField(field, "current")
                  );

                   console.log(currentAddressData, 'currentAddressData')
                   console.log(coApplicantAddressDetails, 'currentAddressData coApplicantAddressDetails')

            const permanentAddressData =
              coApplicantAddressDetails?.permanentAddress &&
              coApplicantAddressDetails.permanentAddress.length > 0
                ? coApplicantAddressDetails.permanentAddress.filter(
                    (field: any) =>
                      !isCoApplicantSameAsAddressField(field) &&
                      (isPartnerJourney ? true : isAddressField(field, "permanent"))
                  )
                : isPartnerJourney ? [] : permanentAddressFields.filter((field: any) =>
                    !isCoApplicantSameAsAddressField(field) &&
                    isAddressField(field, "permanent")
                  );

            return (
              <>
          {currentAddressData.length > 0 ? (
            <>
              <Grid item xs={12} sx={{ mt: 2, mb: 1 }}>
                <SummarySectionHeader title="Current Address" />
              </Grid>
              {currentAddressData.map((field: any, subIndex: number) => {
                let label = field.label || "";
                label = label.replace(/^current\s*/i, "");
                label = label.replace(/(\D)(\d+)/g, "$1 $2");
                label = label.replace(/([a-z])([A-Z])/g, "$1 $2");
                label = label
                  .split(" ")
                  .map((w: string) => (w.length ? w[0].toUpperCase() + w.slice(1) : ""))
                  .join(" ");
                return (
                  <Grid
                    item
                    xs={12}
                    sm={6}
                    md={3}
                    key={`current-${subIndex}`}
                    sx={{ ml: isXsOrSm ? 2 : 0 }}
                  >
                    <DetailRow item={{ label, value: field.value, isVerified: false }} />
                  </Grid>
                );
              })}
            </>
          ) : (
            <Grid item xs={12} sx={{ mt: 2, mb: 1 }}>
              <Typography color="text.secondary" fontSize={14}>
                No address details available.
              </Typography>
            </Grid>
          )}
          {permanentAddressData.length > 0 ? (
            <>
              <Grid item xs={12} sx={{ mt: 2, mb: 1 }}>
                <SummarySectionHeader title="Permanent Address" />
              </Grid>
              {permanentAddressData.map((field: any, subIndex: number) => {
                let label = field.label || "";
                label = label.replace(/^Permanent\s*/i, "");
                label = label.replace(/(\D)(\d+)/g, "$1 $2");
                label = label.replace(/([a-z])([A-Z])/g, "$1 $2");
                label = label
                  .split(" ")
                  .map((w: string) => (w.length ? w[0].toUpperCase() + w.slice(1) : ""))
                  .join(" ");
                return (
                  <Grid
                    item
                    xs={12}
                    sm={6}
                    md={3}
                    key={`permanent-${subIndex}`}
                    sx={{ ml: isXsOrSm ? 2 : 0 }}
                  >
                    <DetailRow item={{ label, value: field.value, isVerified: false }} />
                  </Grid>
                );
              })}
            </>
          ) : null}
              </>
            );
          })()}
        </>
      )}
    </Grid>
  );

  const renderEmploymentSection = () => (
    <Grid container spacing={2}>
      {loading ? (
        tabContentSkeleton()
      ) : (
        <>
          {(() => {
            const rawEmploymentDetailsData =
              coApplicantEmploymentDetails?.EmploymentDetails &&
              Array.isArray(coApplicantEmploymentDetails.EmploymentDetails) &&
              coApplicantEmploymentDetails.EmploymentDetails.length > 0
                ? coApplicantEmploymentDetails.EmploymentDetails.filter((field: any) =>
                    isPartnerJourney ? true : isEmploymentField(field)
                  )
                : isPartnerJourney ? [] : employmentDetailsFields.filter((field: any) =>
                    isEmploymentField(field)
                  );
            const employmentDetailsData = rawEmploymentDetailsData.filter(
              (field: any) => !isCompanyAddressField(field)
            );
                   
            const rawCompanyAddressData =
              coApplicantEmploymentDetails?.CompanyAddressDetails &&
              coApplicantEmploymentDetails?.CompanyAddressDetails?.length > 0
                ? coApplicantEmploymentDetails.CompanyAddressDetails.filter(
                    (field: any) => isPartnerJourney ? true : isCompanyAddressField(field)
                  )
                : isPartnerJourney ? [] : companyAddressFields;
            const companyAddressData = [
              ...rawEmploymentDetailsData.filter(isCompanyAddressField),
              ...rawCompanyAddressData.filter(isCompanyAddressField),
            ].sort(
              (a: any, b: any) =>
                getCompanyAddressFieldOrder(a) - getCompanyAddressFieldOrder(b)
            );
            const isEmployedField = (f: any) =>
                /(^isEmployed$|employmentCoApplicant\d+isEmployed)/i.test(
                  String(f?.name || f?.key || "")
                );
            const displayEmploymentDetailsData = employmentDetailsData.filter(
              (field: any, index: number, arr: any[]) =>
                !isEmployedField(field) ||
                index === arr.findIndex((item: any) => isEmployedField(item))
            );
            const isEmployedNo = displayEmploymentDetailsData.some(
              (f: any) =>
                isEmployedField(f) &&
                (f.value === false || String(f.value || "").toLowerCase() === "no")
            );
            const isFarmerOrOtherEmploymentType = employmentDetailsData.some(
              (f: any) =>
                /(^employmentType$|employmentCoApplicant\d+employmentType)/i.test(
                  String(f?.name || f?.key || "")
                ) &&
                ['farmer', 'other', 'others'].includes(String(f.value || "").toLowerCase())
            );

            return (
              <>
                {displayEmploymentDetailsData.length > 0 ? (
                  <>
                    <Grid item xs={12} sx={{ mt: 2, mb: 1 }}>
                      <SummarySectionHeader title="Employment Details" />
                    </Grid>
                    {displayEmploymentDetailsData
                      .filter(
                        (field: any) => {
                          const fieldName = String(field?.name || field?.key || "");
                          if (isEmployedNo) {
                            return isEmployedField(field);
                          }
                          return isPartnerJourney || !isFarmerOrOtherEmploymentType ||
                          /(isEmployed|employmentType|monthlyIncome|monthlyObligation)$/.test(
                            fieldName
                          );
                        }
                      )
                      .map((field: any, idx: number) => (
                        <Grid
                          item
                          xs={12}
                          sm={6}
                          md={3}
                          key={`emp-${idx}`}
                          sx={{ ml: isXsOrSm ? 2 : 0 }}
                        >
                          <DetailRow
                            item={{
                              label: getEmploymentDisplayLabel(field),
                              value:
                                /isEmployed/i.test(
                                  field?.name || field?.key || field?.label || ''
                                ) && typeof field.value === "boolean"
                                  ? field.value
                                    ? "Yes"
                                    : "No"
                                  : /designation/i.test(
                                      field?.name || field?.key || field?.label || ''
                                    )
                                    ? getDesignationLabel(field.value)
                                    : field.value,
                              isVerified: false,
                            }}
                          />
                        </Grid>
                      ))}
                  </>
                ) : (
                  <Grid item xs={12} sx={{ mt: 2, mb: 1 }}>
                    <Typography color="text.secondary" fontSize={14}>
                      No employment details available.
                    </Typography>
                  </Grid>
                )}
                {!isEmployedNo && !isFarmerOrOtherEmploymentType && companyAddressData.length > 0 ? (
                  <>
                    <Grid item xs={12} sx={{ mt: 2, mb: 1 }}>
                      <SummarySectionHeader title="Company Address" />
                    </Grid>
                    {companyAddressData.map((field: any, idx: number) => (
                      <Grid
                        item
                        xs={12}
                        sm={6}
                        md={3}
                        key={`comp-${idx}`}
                        sx={{ ml: isXsOrSm ? 2 : 0 }}
                      >
                        <DetailRow
                          item={{
                            label: getCompanyAddressDisplayLabel(field),
                            value: field.value,
                            isVerified: false,
                          }}
                        />
                      </Grid>
                    ))}
                  </>
                ) : null}
              </>
            );
          })()}
        </>
      )}
    </Grid>
  );

  const renderSectionContent = (sectionIndex: number) => {
    if (sectionIndex === 0) {
      return renderBasicSection();
    }
    if (sectionIndex === 1) {
      return renderAddressSection();
    }
    return renderEmploymentSection();
  };

  const isSummaryView = pageType === "SUMMARY";
  const triggerCoApplicantEdit = (
    event?: React.MouseEvent<HTMLElement>
  ) => {
    event?.stopPropagation();
    if (editDisabled) return;
    onEditClick?.(coApplicantNumber);
  };

  const tabBlock = (
    <>
      <Tabs
        value={tabIndex}
        onChange={handleTabChange}
        variant={isXsOrSm ? "scrollable" : "standard"}
        {...(isXsOrSm && {
          scrollButtons: "auto",
          allowScrollButtonsMobile: false,
        })}
        sx={{
          mb: 2,
          flexGrow: 1,
          "& .MuiTabs-indicator": {
            backgroundColor: "#00b3b5",
          },
          "& .MuiTab-root": {
            textTransform: "none",
            fontWeight: 600,
            color: "#616161",
            "&.Mui-selected": {
              color: "#00b3b5",
            },
          },
        }}
      >
        {tabLabels?.map((label) => (
          <Tab key={label} label={label} />
        ))}
      </Tabs>
      {tabIndex === 0 && (
        <Box
          sx={{
            backgroundColor: "#EAF7F9",
            borderRadius: "8px",
            px: 2,
            py: 1,
            mb: 2,
          }}
        >
          <Typography
            sx={{
              fontFamily: "Jost, sans-serif",
              fontSize: "15px",
              fontWeight: 600,
              color: "#2F2A3C",
              lineHeight: "22px",
            }}
          >
            {tabLabels[tabIndex]}
          </Typography>
        </Box>
      )}
      <Box>{renderSectionContent(tabIndex)}</Box>
    </>
  );

  if (isSummaryView) {
    return (
      <Box
        sx={{
          border: "1px solid #d6eef1",
          borderRadius: "8px",
          backgroundColor: "#fff",
          overflow: "hidden",
          mb: 2,
        }}
      >
        <Box
          sx={{
            px: 2,
            height: "60px",
            backgroundColor: "#eaf7f9",
            borderBottom: "1px solid #d6eef1",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1.5,
            cursor: "pointer",
          }}
          onClick={onToggle}
        >
          <Typography
            sx={{
              fontFamily: "Jost, sans-serif",
              fontSize: "14px",
              fontWeight: 600,
              color: "#354052",
            }}
          >
            {title}
          </Typography>
          {onEditClick && !hideEditAction && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Button
              variant="text"
              size="small"
              disabled={editDisabled}
              title={
                editDisabled
                  ? "Complete previous sections first"
                  : undefined
              }
              onClick={triggerCoApplicantEdit}
              sx={{
                minWidth: "auto",
                p: 0,
                fontFamily: "Jost, sans-serif",
                fontSize: "13px",
                fontWeight: 600,
                lineHeight: "16px",
                letterSpacing: "0.43px",
                color: "#00b3b5",
                textTransform: "none",
                "&:hover": {
                  backgroundColor: "transparent",
                  color: "#00b3b5",
                },
              }}
            >
              Edit
            </Button>
            </Box>
          )}
        </Box>
        {isExpanded && <Box sx={{ p: 2 }}>{tabBlock}</Box>}
      </Box>
    );
  }

  return (
    <CollapsibleSection
      title={title}
      isExpanded={isExpanded}
      onToggle={onToggle}
      isCompleted={false}
      isProvisionalOfferTab={false}
      provisionalOfferBtnClick={() => {}}
    >
      {tabBlock}
    </CollapsibleSection>
  );
};

interface CoApplicantTabsProps {
  coApplicant: any;
  pageType?: string;
  onEditClick?: (coApplicantNumber: number, displayIndex?: number) => void;
  /** Summary: disable Edit until prior application stages are complete. */
  editDisabled?: boolean;
  designationsVal?: any[];
  hideEditAction?: boolean;
  /** Summary co-applicant section: show dynamic form only on the row matching this co-applicant number (1-based). */
  renderInlineEditForm?: boolean;
  activeInlineEditCoApplicantNumber?: number | null;
  /** Callback to open a new co-applicant in inline edit mode. */
  onAddCoApplicant?: (
    event: React.MouseEvent<HTMLButtonElement>,
    coApplicantNumber: number,
    displayIndex?: number
  ) => void;
  /** Disable the Add More button while loading */
  addCoApplicantLoading?: boolean;
  /** Disable the Add More button based on logic */
  disableAddMoreCoApplicant?: boolean;
  /** Callback invoked when a new co-applicant remove was detected (cleanup local state). */
  onCoApplicantRemoved?: () => void;
}

const MAX_CO_APPLICANTS = 9;

const CoApplicantTabs: React.FC<CoApplicantTabsProps> = ({
  coApplicant,
  pageType = "DEFAULT",
  onEditClick,
  editDisabled = false,
  designationsVal = [],
  hideEditAction = false,
  renderInlineEditForm = false,
  activeInlineEditCoApplicantNumber = null,
  onAddCoApplicant,
  addCoApplicantLoading = false,
  disableAddMoreCoApplicant = false,
  onCoApplicantRemoved,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const [expandedIdx, setExpandedIdx] = useState<number | null>(null);
  const [pennantRelMap, setPennantRelMap] = useState<Record<string, string>>({});
  const [pendingBasicCoApplicantNumber, setPendingBasicCoApplicantNumber] =
    useState<number | null>(null);
  const autoOpenedDraftRef = React.useRef<number | null>(null);

  const {
    fetchUserApplicationData,
    formJsonRefreshNonce,
    trackingId,
    horizontalSteps,
    savedBasicCoApplicants,
  } = useReduxSelector(
    (state: ReduxRootState) => state.stepper
  );

  useEffect(() => {
    if (!pendingBasicCoApplicantNumber) return;
    if (savedBasicCoApplicants?.[`CoApplicant${pendingBasicCoApplicantNumber}`]) {
      setPendingBasicCoApplicantNumber(null);
    }
  }, [pendingBasicCoApplicantNumber, savedBasicCoApplicants]);

  useEffect(() => {
    losServices.relationMaster().then((data: any) => {
      if (data?.status === 200) {
        const map: Record<string, string> = {};
        data?.data?.data?.forEach((item: any) => {
          if (item.codeInPennant) map[item.codeInPennant] = item.descInPennant;
        });
        setPennantRelMap(map);
      }
    }).catch(() => {});
  }, []);
  
  // commented for future reasong in case any issue comes
  // let label = fieldObj?.label || stripPrefixAndFormat(key);
  //     if (/OtherRelationWithStudent/i.test(key)) label = "Specify Relationship";
  //     else if (/RelationWithStudent/i.test(key)) label = "Relationship with Student";
  //     else if (/FirstName/i.test(key)) label = "Co-Applicant First Name";
  //     else if (/LastName/i.test(key)) label = "Co-Applicant Last Name";
  //     else if (/PanNumber/i.test(key)) label = "PAN Number";
  //     else if (/IsPanVerified/i.test(key)) label = "PAN Verified";
  //     else if (/Email$/i.test(key) && !/IsEmailVerified/i.test(key))
  //       label = "Email ID";
  //     else if (/IsEmailVerified/i.test(key)) label = "Email Verified";
  //     else if (/Phone_number/i.test(key)) label = "Mobile Number";
  //     else if (/IsMobileVerified/i.test(key)) label = "Mobile Verified";

  //     // Use correct name property for output
  //     let name = key;
  //     if (/OtherRelationWithStudent/i.test(key))
  //       name = `coApplicant${coAppNum}OtherRelationWithStudent`;
  //     else if (/RelationWithStudent/i.test(key))
  //       name = `coApplicant${coAppNum}RelationWithStudent`;
  //     else if (/Name/i.test(key)) name = `coApplicant${coAppNum}Name`;
  //     else if (/PanNumber/i.test(key)) name = `coApplicant${coAppNum}PanNumber`;
  //     else if (/IsPanVerified/i.test(key))
  //       name = `coApplicant${coAppNum}IsPanVerified`;
  //     else if (/Email$/i.test(key) && !/IsEmailVerified/i.test(key))
  //       name = `coApplicant${coAppNum}Email`;
  //     else if (/IsEmailVerified/i.test(key))
  //       name = `coApplicant${coAppNum}IsEmailVerified`;
  //     else if (/Phone_number/i.test(key))
  //       name = `coApplicant${coAppNum}Phone_number`;
  //     else if (/IsMobileVerified/i.test(key))
  //       name = `coApplicant${coAppNum}IsMobileVerified`;

  //     coAppMap[coAppKey].push({
  //       label,
  //       name,
  //       key,
  //       value,
  //     });

  //     // Optionally, extract co-applicant ID if present
  //     if (/Id$/.test(key)) {
  //       coAppIdMap[coAppKey] = value;
  //     }
  //   });

  //   // Build the final array, sorted by co-applicant number
  //   const grouped: any[] = Object.keys(coAppMap)
  //     .sort((a, b) => {
  //       // Extract numbers from keys like 'coapplicant1', 'coapplicant2', etc.
  //       const numA = parseInt(a.replace(/[^\d]/g, ""), 10);
  //       const numB = parseInt(b.replace(/[^\d]/g, ""), 10);
  //       return numA - numB;
  //     })
  //     .map((coAppKey) => ({
  //       [coAppKey]: coAppMap[coAppKey],
  //       [`${coAppKey}Id`]: coAppIdMap[coAppKey],
  //     }));

  //   return grouped;
  // }

  let coApplicantToShow = coApplicant;
  if (
    pageType === "SUMMARY" &&
    fetchUserApplicationData &&
    !fetchUserApplicationData?.partnerStartedJourney
  ) {
    // Find the 'Applicant & Loan Details' horizontal step
    const applicantLoanStep = fetchUserApplicationData?.horizontalSteps?.find(
      (h: any) => h.horizontalStepName === "Applicant & Loan Details"
    );
    // Find the old 'Loan Details' / new 'Co-Applicant Details' vertical step (Source 1)
    const loanDetailsStep = applicantLoanStep?.verticalSteps?.find(
      (v: any) =>
        Number(v?.verticalStepId) === 9 ||
        v.verticalStepName === "Loan Details" ||
        v.verticalStepName === "Co-Applicant Details"
    );
    const mappedCoApplicantsFromLoanDetails = mapCoApplicantDetailsFromVerticalStep(
      loanDetailsStep
    );

    // Find the 'Additional Details & Documents' horizontal step (Source 2)
    const additionalDetailsStep = fetchUserApplicationData?.horizontalSteps?.find(
      (h: any) => h.horizontalStepName === "Additional Details & Documents"
    );
    // Find the 'Co-Applicant Additional Details' vertical step
    const coApplicantAdditionalDetailsStep = additionalDetailsStep?.verticalSteps?.find(
      (v: any) => {
        const name = v?.verticalStepName || "";
        return name === "Co-Applicant Additional Details" || name === "Co-Applicant Aditional Details";
      }
    );
    const mappedCoApplicantsFromAdditionalDetails = mapCoApplicantDetailsFromVerticalStep(
      coApplicantAdditionalDetailsStep
    );

    // Merge Co-Applicants from both sources
    const mergedFromBothSources = mergeCoApplicantDetails(
      mappedCoApplicantsFromLoanDetails || [],
      mappedCoApplicantsFromAdditionalDetails || []
    );

    const fallbackCoApplicants = transformCoApplicantObjectToArray(coApplicant) || [];
    coApplicantToShow = mergeCoApplicantDetails(
      mergedFromBothSources || [],
      Array.isArray(fallbackCoApplicants) ? fallbackCoApplicants : []
    );
  }

  const fetchCoApplicants = transformCoApplicantObjectToArray(coApplicantToShow);
  const getDataCoApplicantStep =
    horizontalSteps?.[1]?.verticalSteps?.[5] ||
    horizontalSteps
      ?.flatMap((step: any) => step?.verticalSteps || [])
      ?.find((step: any) =>
        /co-applicant/i.test(
          String(step?.verticalStepName || step?.stepLabel || "")
        )
      );
  const getDataCoApplicants = mapCoApplicantDetailsFromFormStep(
    getDataCoApplicantStep
  );

  coApplicantToShow =
    pageType === "SUMMARY"
      ? fetchUserApplicationData?.partnerStartedJourney
        ? fetchCoApplicants
        : mergeCoApplicantDetails(
            Array.isArray(fetchCoApplicants) ? fetchCoApplicants : [],
            getDataCoApplicants
          )
      : fetchCoApplicants;

  useEffect(() => {
    if (renderInlineEditForm) {
      setExpandedIdx(null);
      return;
    }
    if (!Array.isArray(coApplicantToShow) || coApplicantToShow.length === 0) {
      setExpandedIdx(null);
      return;
    }
    setExpandedIdx((currentIdx) =>
      currentIdx == null || currentIdx >= coApplicantToShow.length
        ? 0
        : currentIdx
    );
  }, [
    Array.isArray(coApplicantToShow) ? coApplicantToShow.length : 0,
    renderInlineEditForm,
  ]);

  // Build the set of co-applicant numbers from the merged data source.
  const mergedCoApplicantNums = new Set<number>();
  if (Array.isArray(coApplicantToShow)) {
    coApplicantToShow.forEach((item: any) => {
      const key = Object.keys(item || {}).find((k) =>
        String(k).toLowerCase().startsWith("coapplicant")
      );
      if (key) {
        const num = parseInt(String(key).replace(/[^\d]/g, ""), 10);
        if (!isNaN(num)) mergedCoApplicantNums.add(num);
      }
    });
  }

  const fetchCoApplicantNums = new Set<number>();
  if (Array.isArray(fetchCoApplicants)) {
    fetchCoApplicants.forEach((item: any) => {
      const key = Object.keys(item || {}).find((k) =>
        String(k).toLowerCase().startsWith("coapplicant")
      );
      const num = key ? parseInt(String(key).replace(/[^\d]/g, ""), 10) : NaN;
      if (!isNaN(num)) fetchCoApplicantNums.add(num);
    });
  }

  const getDataOnlyCoApplicantNums = Array.from(mergedCoApplicantNums)
    .filter((num) => !fetchCoApplicantNums.has(num))
    .sort((a, b) => a - b);
  const getCoApplicantFieldsByNumber = (
    coApplicants: any[],
    coApplicantNumber: number
  ) => {
    const targetKey = `coapplicant${coApplicantNumber}`;
    const row = (coApplicants || []).find((item: any) =>
      Object.keys(item || {}).some(
        (key) => String(key).toLowerCase() === targetKey
      )
    );
    const rowKey = Object.keys(row || {}).find(
      (key) => String(key).toLowerCase() === targetKey
    );
    return rowKey ? row?.[rowKey] || [] : [];
  };
  const hasMeaningfulCoApplicantValue = (field: any) => {
    const fieldName = String(field?.name || field?.key || "").toLowerCase();
    if (
      /verified|aadhaar|pannumber|id$/.test(fieldName) ||
      fieldName === "name"
    ) {
      return false;
    }
    const rawValue =
      field?.value ??
      (field?.key && field?.[field.key]) ??
      field?.fieldValue ??
      "";
    if (rawValue == null) return false;
    if (typeof rawValue === "boolean") return rawValue;
    if (typeof rawValue === "object") {
      return Boolean(rawValue?.value || rawValue?.label);
    }
    return String(rawValue).trim() !== "";
  };
  const isCoApplicantDraftCleared = (coApplicantNumber: number) =>
    typeof window !== "undefined" &&
    window.sessionStorage.getItem(
      `coApplicantEmploymentSaved:${trackingId}:${coApplicantNumber}`
    ) === "true";

  const pendingDraftCoApplicantNums = getDataOnlyCoApplicantNums.filter(
    (num) => {
      const fields = getCoApplicantFieldsByNumber(getDataCoApplicants, num);
      return (
        fields.length > 0 &&
        !isCoApplicantDraftCleared(num) &&
        !fields.some(hasMeaningfulCoApplicantValue)
      );
    }
  );
  const maxFetchCoApplicantNum = Array.from(fetchCoApplicantNums).reduce(
    (max, num) => Math.max(max, num),
    0
  );
  const newDraftCoApplicantNums = pendingDraftCoApplicantNums.filter(
    (num) => fetchCoApplicantNums.size === 0 || num > maxFetchCoApplicantNum
  );
  const latestNewDraftCoApplicantNumber =
    newDraftCoApplicantNums[newDraftCoApplicantNums.length - 1] ?? null;

  useEffect(() => {
    if (pageType !== "SUMMARY") return;
    if (renderInlineEditForm) return;
    if (!latestNewDraftCoApplicantNumber) return;
    if (fetchUserApplicationData?.partnerStartedJourney) return;
    if (autoOpenedDraftRef.current === latestNewDraftCoApplicantNumber) return;

    autoOpenedDraftRef.current = latestNewDraftCoApplicantNumber;
    onEditClick?.(latestNewDraftCoApplicantNumber);
  }, [
    latestNewDraftCoApplicantNumber,
    onEditClick,
    pageType,
    renderInlineEditForm,
  ]);

  const editTargetExists =
    activeInlineEditCoApplicantNumber == null ||
    mergedCoApplicantNums.has(activeInlineEditCoApplicantNumber);

  const hasReachedMaxCoApplicants =
    Array.isArray(coApplicantToShow) &&
    coApplicantToShow.length >= MAX_CO_APPLICANTS;

  const handleAddCoApplicant = useCallback(async (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    if (hasReachedMaxCoApplicants) return;

    let nextCoApplicantNum = 0;
    try {
      const countResp = await postsServices.addPost(
        "api/v1/applicant/get-coapplicant-count",
        { trackingId }
      );
      const apiCount = countResp?.data?.data?.count;
      if (typeof apiCount === "number") {
        nextCoApplicantNum = apiCount + 1;
      } else {
        const maxMergedNumber = Array.from(mergedCoApplicantNums).reduce(
          (max, num) => Math.max(max, num),
          0
        );
        nextCoApplicantNum = maxMergedNumber + 1;
      }
    } catch (error) {
      const maxMergedNumber = Array.from(mergedCoApplicantNums).reduce(
        (max, num) => Math.max(max, num),
        0
      );
      nextCoApplicantNum = maxMergedNumber + 1;
    }

    if (onAddCoApplicant) {
      onAddCoApplicant(event, nextCoApplicantNum, coApplicantToShow.length + 1);
      return;
    }

    setPendingBasicCoApplicantNumber(nextCoApplicantNum);
    dispatch(setSummaryEditOpenedFromCoApplicantSection(true));
    dispatch(setActiveHorizontalIndex(1));
    dispatch(setActiveVerticalIndex(5));
    dispatch(setLoanDetailCoApplicantCollapseValue(`CoApplicant${nextCoApplicantNum}`));
    dispatch(setSummaryApplicationEditActive(true));
  }, [
    dispatch,
    hasReachedMaxCoApplicants,
    mergedCoApplicantNums,
    onAddCoApplicant,
    trackingId,
  ]);

  if (!Array.isArray(coApplicantToShow)) {
    return (
      <Grid item xs={12}>
        <Typography variant="body2" color="textSecondary">
          No details available.
        </Typography>
      </Grid>
    );
  }

  const shouldRenderNewInlineEdit =
    renderInlineEditForm &&
    activeInlineEditCoApplicantNumber != null &&
    !editTargetExists;

  const inlineEditForm = (
    <Box
      key={`coapp-inline-new-${activeInlineEditCoApplicantNumber ?? "active"}`}
      sx={{
        border: "1px solid #d6eef1",
        borderRadius: "8px",
        backgroundColor: "#fff",
        overflow: "hidden",
        mb: 2,
      }}
    >
      <Box
        sx={{
          px: { xs: 1, sm: 2 },
          py: 2,
          backgroundColor: "transparent",
        }}
      >
        <HorizontalLinearStepper
          key={`summary-hls-${formJsonRefreshNonce}-${activeInlineEditCoApplicantNumber ?? "new"}`}
          status={true}
          formPageType="SUMMARY"
          module="ASSSITED"
        />
      </Box>
    </Box>
  );


  return (
    <>
      {/* Hide Add More button when inline edit form is active (only newly added should be editable) */}
      {!hideEditAction && (
        <Grid container justifyContent="end" sx={{ mx: 0, mb: 2 }}>
          <Button
            sx={{
              textTransform: "none",
              fontFamily: "Jost, sans-serif",
              fontWeight: 600,
              fontSize: "14px",
              lineHeight: "100%",
              letterSpacing: "0%",
              color: "#33c2c4",
              borderColor: "#33c2c4",
              "&:hover": {
                backgroundColor: "transparent",
                borderColor: "#33c2c4",
                color: "#33c2c4",
              },
            }}
            onClick={handleAddCoApplicant}
            disabled={
              // addCoApplicantLoading ||
              // renderInlineEditForm
              // || disabledAddMoreCoapplicant
              addCoApplicantLoading ||
              disableAddMoreCoApplicant ||
              pendingDraftCoApplicantNums.length > 0 ||
              Boolean(pendingBasicCoApplicantNumber) ||
              hasReachedMaxCoApplicants
            }
          >
            {addCoApplicantLoading ? (
              <CircularProgress size={16} sx={{ mr: 1, color: "#33c2c4" }} />
            ) : (
              <AddIcon sx={{ mr: 1, fontSize: "20px" }} />
            )}
            Add More
          </Button>
        </Grid>
      )}
      {shouldRenderNewInlineEdit && inlineEditForm}
      {coApplicantToShow?.length > 0 ? (
        coApplicantToShow?.map((coAppObj: any, idx: number) => {
          // Find the key like 'coapplicant1', 'coapplicant2', etc.
          const coAppKey = Object.keys(coAppObj).find((k) =>
            k.startsWith("coapplicant")
          );
          const coAppFields = coAppKey ? coAppObj[coAppKey] : [];
          const coAppId = coAppObj[`${coAppKey}Id`];
          const coApplicantNumberFromKey = coAppKey
            ? Number(String(coAppKey).replace(/[^\d]/g, ""))
            : NaN;
          const effectiveCoApplicantNumber = Number.isFinite(
            coApplicantNumberFromKey
          )
            ? coApplicantNumberFromKey
            : idx + 1;

          if (!coAppFields || coAppFields.length === 0) {
            return (
              <Grid item xs={12} key={coAppId || idx}>
                <Typography variant="body2" color="textSecondary">
                  No details available.
                </Typography>
              </Grid>
            );
          }

          const isThisRowInlineEdit =
            renderInlineEditForm &&
            activeInlineEditCoApplicantNumber != null &&
            activeInlineEditCoApplicantNumber === effectiveCoApplicantNumber;

          if (isThisRowInlineEdit) {
            return inlineEditForm;
          }

          return (
            <SingleCoApplicantTabs
              key={coAppId || idx}
              coApplicantFields={coAppFields}
              coApplicantId={coAppId}
              coApplicantNumber={effectiveCoApplicantNumber}
              title={`Co-Applicant ${idx + 1}`}
              isExpanded={expandedIdx === idx}
              onToggle={() => setExpandedIdx(expandedIdx === idx ? null : idx)}
              pageType={pageType}
              editDisabled={editDisabled}
              pennantRelMap={pennantRelMap}
              designationsVal={designationsVal}
              hideEditAction={hideEditAction}
              onEditClick={
                onEditClick && !hideEditAction
                  ? (num) => {
                      setExpandedIdx(idx);
                      onEditClick(num, idx + 1);
                    }
                  : undefined
              }
            />
          );
        })
      ) : (
        !shouldRenderNewInlineEdit && <Grid item xs={12}>
          <Typography variant="body2" color="textSecondary">
            No details available.
          </Typography>
        </Grid>
      )}
    </>
  );
};

export default CoApplicantTabs;
